/**
 * Directional peer logout cookies (company ↔ expert on shared apex Domain).
 * - Company navbar sets FOR_EXPERT only (expert app reads & clears those).
 * - Company `react` reads FOR_COMPANY only (signals from expert).
 * - Sign-in clears only FOR_COMPANY + legacy names, not FOR_EXPERT (outgoing to expert).
 *
 * VUE_APP_CROSS_APP_LOGOUT_COOKIE_DOMAIN is inlined at **build** time (Vue CLI).
 */

import { useUserStore } from "@/stores/user";
import { cognitoSignOut } from "@/cognito/auth";

/** Written by company on logout; consumed only by the expert SPA. */
const PEER_LOGOUT_FOR_EXPERT_SESSION = "slashexperts_peer_expert";
const PEER_LOGOUT_FOR_EXPERT_SUB = "slashexperts_peer_expert_sub";
const PEER_LOGOUT_FOR_EXPERT_EMAIL = "slashexperts_peer_expert_email";

/** Written by expert on logout; consumed only by the company SPA. */
const PEER_LOGOUT_FOR_COMPANY_SESSION = "slashexperts_peer_company";
const PEER_LOGOUT_FOR_COMPANY_SUB = "slashexperts_peer_company_sub";
const PEER_LOGOUT_FOR_COMPANY_EMAIL = "slashexperts_peer_company_email";

/** One-time SSO handoff (separate keys per direction). */
const PEER_HANDOFF_TO_COMPANY_EMAIL = "slashexperts_handoff_to_company_email";
const PEER_HANDOFF_TO_EXPERT_EMAIL = "slashexperts_handoff_to_expert_email";
/** Legacy single-target email cookie — read/clear once for migration. */
const LEGACY_PEER_HANDOFF_EMAIL = "slashexperts_peer_handoff_email";
const PEER_HANDOFF_TARGET = "slashexperts_peer_handoff_target";
const PEER_HANDOFF_SESSION = "slashexperts_peer_handoff_session";
const PEER_HANDOFF_SESSION_CHUNKS = "slashexperts_peer_handoff_session_n";
const PEER_HANDOFF_USE_IMPERSONATION =
  "slashexperts_peer_handoff_use_impersonation";

export type CrossAppHandoffTarget = "company" | "expert";

/** Pre-directional names — cleared opportunistically so old tabs don’t leave junk. */
const LEGACY_SESSION = "slashexperts_logout_session";
const LEGACY_SUB = "slashexperts_logout_sub";
const LEGACY_ISSUER = "slashexperts_logout_issuer";

const COOKIE_MAX_AGE_SEC = 86400;
const HANDOFF_COOKIE_MAX_AGE_SEC = 1800;

function browserHostname(): string {
  const win = globalThis.window;
  if (win === undefined) {
    return "";
  }
  return win.location.hostname;
}

function domainAttrForCookie(): string {
  const raw = (process.env.VUE_APP_CROSS_APP_LOGOUT_COOKIE_DOMAIN || "").trim();
  if (raw) {
    const d = raw.startsWith(".") ? raw : `.${raw}`;
    return `; Domain=${d}`;
  }
  const host = browserHostname();
  if (host === "localhost" || host === "127.0.0.1") {
    return "";
  }
  return "";
}

function secureSameSitePath(): string {
  const secure =
    globalThis.window?.location.protocol === "https:" ? "; Secure" : "";
  return `; Path=/; SameSite=Lax${secure}`;
}

function readCookieMap(): Map<string, string> {
  const map = new Map<string, string>();
  if (typeof document === "undefined") return map;
  const parts = document.cookie.split(";");
  for (const part of parts) {
    const idx = part.indexOf("=");
    if (idx === -1) continue;
    const k = part.slice(0, idx).trim();
    const v = part.slice(idx + 1).trim();
    map.set(k, decodeURIComponent(v));
  }
  return map;
}

function readPeerLogoutState(
  sessionKey: string,
  subKey: string
): {
  logoutSession: boolean;
  sub: string | null;
} {
  const map = readCookieMap();
  const flag = map.get(sessionKey);
  const subRaw = map.get(subKey);
  return {
    logoutSession: flag === "1",
    sub: subRaw?.trim() ? subRaw.trim() : null,
  };
}

function clearTwoPeerCookies(sessionKey: string, subKey: string): void {
  try {
    const dom = domainAttrForCookie();
    const tail = `${dom}${secureSameSitePath()}; Max-Age=0`;
    document.cookie = `${sessionKey}=${tail}`;
    document.cookie = `${subKey}=${tail}`;
  } catch {
    /* ignore */
  }
}

function clearCookie(name: string): void {
  try {
    const dom = domainAttrForCookie();
    const tail = `${dom}${secureSameSitePath()}; Max-Age=0`;
    document.cookie = `${name}=${tail}`;
  } catch {
    /* ignore */
  }
}

function readPeerHandoffSessionFromMap(map: Map<string, string>): string {
  const single = (map.get(PEER_HANDOFF_SESSION) || "").trim();
  if (single) return single;
  const chunkCount = Number.parseInt(
    map.get(PEER_HANDOFF_SESSION_CHUNKS) || "",
    10
  );
  if (!Number.isFinite(chunkCount) || chunkCount <= 0) return "";
  let encoded = "";
  for (let i = 0; i < chunkCount; i += 1) {
    encoded += map.get(`${PEER_HANDOFF_SESSION}_${i}`) || "";
  }
  return encoded.trim();
}

function clearPeerHandoffSessionCookies(): void {
  clearCookie(PEER_HANDOFF_SESSION);
  clearCookie(PEER_HANDOFF_SESSION_CHUNKS);
  clearCookie(PEER_HANDOFF_USE_IMPERSONATION);
  try {
    const map = readCookieMap();
    for (const key of map.keys()) {
      if (key.startsWith(`${PEER_HANDOFF_SESSION}_`)) {
        clearCookie(key);
      }
    }
  } catch {
    /* ignore */
  }
}

/** Hint the peer SPA to start its URL handoff flow (consume once). */
export function setCrossAppHandoffCookie(
  email: string,
  target: CrossAppHandoffTarget
): void {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return;
  const key =
    target === "expert"
      ? PEER_HANDOFF_TO_EXPERT_EMAIL
      : PEER_HANDOFF_TO_COMPANY_EMAIL;
  try {
    const dom = domainAttrForCookie();
    const tail = `${dom}${secureSameSitePath()}; Max-Age=${HANDOFF_COOKIE_MAX_AGE_SEC}`;
    document.cookie = `${key}=${encodeURIComponent(normalized)}${tail}`;
  } catch {
    /* ignore */
  }
}

/** Refresh handoff cookie while company session is switchable (new expert tab / SSO). */
export function syncSwitchableCompanyHandoffCookie(
  email: string | null | undefined,
  switchable: boolean
): void {
  if (!switchable) {
    clearCookie(PEER_HANDOFF_TO_EXPERT_EMAIL);
    return;
  }
  // Company→expert handoff is email-only; drop any legacy session chunks.
  clearPeerHandoffSessionCookies();
  const normalized = (email || "").trim();
  if (!normalized) return;
  setCrossAppHandoffCookie(normalized, "expert");
}

/** Drop expert→company handoff cookies without starting sign-in. */
export function clearExpertToCompanyHandoffCookies(): void {
  clearCookie(PEER_HANDOFF_TO_COMPANY_EMAIL);
  clearCookie(LEGACY_PEER_HANDOFF_EMAIL);
  clearCookie(PEER_HANDOFF_TARGET);
  clearPeerHandoffSessionCookies();
}

/** Clear both directions of handoff cookies during explicit logout. */
export function clearCompanyCrossAppHandoffCookiesForLogout(): void {
  clearCookie(PEER_HANDOFF_TO_EXPERT_EMAIL);
  clearExpertToCompanyHandoffCookies();
}

export type CrossAppHandoffPayload = {
  email: string;
  target: CrossAppHandoffTarget;
  sessionToken: string | null;
  useImpersonation: boolean;
};

function readInboundExpertToCompanyHandoffFromMap(
  map: Map<string, string>
): CrossAppHandoffPayload | null {
  let email = (map.get(PEER_HANDOFF_TO_COMPANY_EMAIL) || "")
    .trim()
    .toLowerCase();
  if (!email) {
    const legacyTarget = map.get(PEER_HANDOFF_TARGET)?.trim();
    if (legacyTarget === "company") {
      email = (map.get(LEGACY_PEER_HANDOFF_EMAIL) || "").trim().toLowerCase();
    }
  }
  if (!email) {
    return null;
  }
  const sessionToken = readPeerHandoffSessionFromMap(map) || null;
  const useImpersonation = map.get(PEER_HANDOFF_USE_IMPERSONATION) === "1";
  return {
    email,
    target: "company",
    sessionToken,
    useImpersonation,
  };
}

/** Read expert→company handoff without clearing (expert tab refreshes this cookie on focus). */
export function peekCrossAppHandoffCookie(): CrossAppHandoffPayload | null {
  return readInboundExpertToCompanyHandoffFromMap(readCookieMap());
}

export function peekCrossAppHandoffEmail(): string | null {
  return peekCrossAppHandoffCookie()?.email ?? null;
}

/** Expert app → company cold open (company SPA only). */
/** @deprecated Prefer peekCrossAppHandoffCookie — clearing before SSO breaks retries. */
export function consumeCrossAppHandoffCookie(): CrossAppHandoffPayload | null {
  const handoff = peekCrossAppHandoffCookie();
  if (!handoff) {
    return null;
  }
  clearExpertToCompanyHandoffCookies();
  return handoff;
}

function clearLegacyCookieTriplet(): void {
  try {
    const dom = domainAttrForCookie();
    const tail = `${dom}${secureSameSitePath()}; Max-Age=0`;
    document.cookie = `${LEGACY_SESSION}=${tail}`;
    document.cookie = `${LEGACY_SUB}=${tail}`;
    document.cookie = `${LEGACY_ISSUER}=${tail}`;
  } catch {
    /* ignore */
  }
}

/**
 * Stale signals **to** the company app (from expert) + legacy global triple.
 * Does not clear FOR_EXPERT (signal company sent for expert to consume).
 * Call from Cognito callback after a successful company session.
 */
function clearThreePeerCookies(
  sessionKey: string,
  subKey: string,
  emailKey: string
): void {
  clearTwoPeerCookies(sessionKey, subKey);
  clearCookie(emailKey);
}

function readPeerLogoutEmail(emailKey: string): string | null {
  const trimmed = readCookieMap().get(emailKey)?.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

function peerLogoutMatchesStore(
  cookieSub: string | null,
  cookieEmail: string | null,
  storeSub: string | null,
  storeEmail: string | null
): boolean {
  if (cookieSub && storeSub && cookieSub === storeSub) {
    return true;
  }
  if (cookieEmail && storeEmail && cookieEmail === storeEmail) {
    return true;
  }
  return false;
}

export function clearIncomingPeerLogoutCookiesForCompany(): void {
  clearThreePeerCookies(
    PEER_LOGOUT_FOR_COMPANY_SESSION,
    PEER_LOGOUT_FOR_COMPANY_SUB,
    PEER_LOGOUT_FOR_COMPANY_EMAIL
  );
  clearLegacyCookieTriplet();
}

/** Call from company navbar logout — before Hosted UI logout; asks expert to sign out. */
export function setCrossAppLogoutSignalCookies(
  cognitoSub: string | null | undefined,
  email?: string | null
): void {
  const sub = typeof cognitoSub === "string" ? cognitoSub.trim() : "";
  const normalizedEmail =
    typeof email === "string" ? email.trim().toLowerCase() : "";
  if (!sub && !normalizedEmail) return;
  try {
    const dom = domainAttrForCookie();
    const tail = `${dom}${secureSameSitePath()}; Max-Age=${COOKIE_MAX_AGE_SEC}`;
    document.cookie = `${PEER_LOGOUT_FOR_EXPERT_SESSION}=1${tail}`;
    if (sub) {
      document.cookie = `${PEER_LOGOUT_FOR_EXPERT_SUB}=${encodeURIComponent(
        sub
      )}${tail}`;
    }
    if (normalizedEmail) {
      document.cookie = `${PEER_LOGOUT_FOR_EXPERT_EMAIL}=${encodeURIComponent(
        normalizedEmail
      )}${tail}`;
    }
  } catch {
    /* ignore */
  }
}

const COMPANY_LOGOUT_CHANNEL = "cognito_company_logout";

/**
 * Expert → company signal: if sub matches, logout this app (BC + cognitoSignOut).
 */
export function reactToCrossAppLogoutCookieIfApplicable(): void {
  const { logoutSession, sub: cookieSub } = readPeerLogoutState(
    PEER_LOGOUT_FOR_COMPANY_SESSION,
    PEER_LOGOUT_FOR_COMPANY_SUB
  );
  const cookieEmail = readPeerLogoutEmail(PEER_LOGOUT_FOR_COMPANY_EMAIL);
  if (!logoutSession && !cookieSub && !cookieEmail) return;

  const userStore = useUserStore();
  const storeSub = userStore.cognitoSub?.trim() || null;
  const storeEmail = userStore.email?.trim().toLowerCase() || null;

  if (!logoutSession) {
    clearThreePeerCookies(
      PEER_LOGOUT_FOR_COMPANY_SESSION,
      PEER_LOGOUT_FOR_COMPANY_SUB,
      PEER_LOGOUT_FOR_COMPANY_EMAIL
    );
    return;
  }

  clearThreePeerCookies(
    PEER_LOGOUT_FOR_COMPANY_SESSION,
    PEER_LOGOUT_FOR_COMPANY_SUB,
    PEER_LOGOUT_FOR_COMPANY_EMAIL
  );

  if (!peerLogoutMatchesStore(cookieSub, cookieEmail, storeSub, storeEmail)) {
    return;
  }

  try {
    new BroadcastChannel(COMPANY_LOGOUT_CHANNEL).postMessage({
      type: "logout",
    });
  } catch {
    /* BroadcastChannel unavailable */
  }
  clearCompanyCrossAppHandoffCookiesForLogout();
  void cognitoSignOut();
}
